<script setup lang="ts">
import type { CmsElementBuyBox } from "@shopware-pwa/composables-next";
import { ApiClientError } from "@shopware/api-client";
import {
  buildUrlPrefix,
  getProductRoute,
  getTranslatedProperty,
} from "@shopware-pwa/helpers-next";
import type { Schemas } from "#shopware";

import {
  useCmsElementConfig,
  useProductPrice,
  useSessionContext,
  useProduct,
  usePrice,
  useShopwareContext,
} from "#imports";
import { computed, watch } from "vue";
import SwVariantConfigurator from "@shopware-pwa/cms-base/components/SwVariantConfigurator.vue";
import ProductBasicInfo from "~/components/product/detail/ProductBasicInfo.vue";
import { useWindowSize } from "@vueuse/core";
import { productDetailTracking } from "~/components/utilities/GtmTrackings";
import { ProductDetailProductVariantConfigurator } from "#components";
import { getProductBundles } from "~/components/utilities";
import BundleProductLineItem from "~/components/product/BundleProductLineItem.vue";

const { getPaymentMethods } = useCheckout();
const { getUrlPrefix } = useUrlResolver();
const payments = await getPaymentMethods();

const props = defineProps<{
  content: CmsElementBuyBox;
}>();

const { getConfigValue } = useCmsElementConfig(props.content);
const alignment = computed(() => getConfigValue("alignment"));

const { taxState, currency } = useSessionContext();

const { product, changeVariant } = useProduct(
  props.content.data.product,
  props.content.data.configuratorSettings || []
);

const { unitPrice, price, tierPrices, isListPrice } = useProductPrice(product);
const regulationPrice = computed(() => price.value?.regulationPrice?.price);
const { getFormattedPrice } = usePrice();

const referencePrice = computed(
  () => product.value?.calculatedPrice?.referencePrice
);
const productNumber = computed(() => product.value?.productNumber);
const availableStock = computed(() => product.value?.availableStock ?? 0);
const minPurchase = computed(() => product.value?.minPurchase ?? 0);
const deliveryTime = computed(() => product.value?.deliveryTime);
const restockTime = computed(() => product.value?.restockTime);
const { addMedias } = useProductGallery();

let additionalMedia: Ref<Schemas["ProductMedia"][]> = ref([]);
addMedias(additionalMedia);

const Bundles = await getProductBundles(unref(product));

if (Bundles) {
  Bundles.products.forEach((product: Schemas["Product"]) => {
    if (product.cover && product.cover.media) {
      additionalMedia.value?.push(product.cover);
    }
  });
}

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);
const { pushSuccess, pushError } = useNotifications();
const { t } = useI18n();

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      await addToWishlist();
      return pushSuccess(
        t(`product.messages.addedToWishlist`, {
          p: product.value?.name,
        })
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        const reason = error.details.errors?.[0]?.detail
          ? `Reason: ${error.details.errors?.[0]?.detail}`
          : "";
        return pushError(
          `${product.value?.name} cannot be added to wishlist.\n${reason}`,
          {
            timeout: 5000,
          }
        );
      }
    }
  }
  removeFromWishlist();
};

// Function to fetch media details
const { apiClient } = useShopwareContext();
const paymentMediaUrls = ref<string[]>([]);
const fetchMediaDetails = async (mediaIds: string[]): Promise<any[]> => {
  try {
    const response = await apiClient.invoke("readMedia post /media", {
      body: {
        ids: mediaIds,
      },
    });
    return response.data.map((media: any) => media);
  } catch (error) {
    console.error("Failed to fetch media details:", error);
    return [];
  }
};

const { totalPrice } = useCart();

// Watch for changes in payments and fetch media details
watch(
  () => payments.value,
  async () => {
    const mediaIds: string[] = [];
    // Extract media IDs from payments
    payments.value.forEach((item) => {
      if (item.customFields?.custom_payment_icon) {
        mediaIds.push(item.customFields.custom_payment_icon);
      }
    });
    if (mediaIds.length > 0) {
      paymentMediaUrls.value = await fetchMediaDetails(mediaIds);
    }
  },
  { immediate: true }
);

const { width } = useWindowSize();
const isDesktop = computed(() => width.value >= 768);

if (import.meta.client) {
  productDetailTracking(product.value);
}
</script>
<template>
  <div class="product-buy-box">
    <template v-if="product">
      <!-- Product Basic Info - Show only on desktop -->
      <div v-if="isDesktop" class="hidden md:block">
        <ProductBasicInfo :product="product" :alignment="alignment" />
      </div>

      <div class="product-other-info">
        <div class="other-detail mb-4">
          <ul class="space-y-4">
            <li
              v-if="
                restockTime ||
                (availableStock > 0 && availableStock < minPurchase)
              "
              class="flex items-center text-arsenic font-bold text-sm leading-none"
            >
              <span class="mr-3">
                <img
                  src="assets/icon/correct-with-circle-icon.svg"
                  alt="icon"
                />
              </span>
              <span
                v-if="
                  availableStock < minPurchase && deliveryTime && restockTime
                "
              >
                {{ $t("productDetails.deliveryTime") }} {{ restockTime }}
                {{ $t("productDetails.days") }}</span
              >
              <span
                v-else-if="availableStock > 0 && availableStock < minPurchase"
              >
                >{{ $t("product.availableStock", { availableStock }) }}
              </span>
            </li>
            <li
              class="flex items-center text-arsenic font-bold text-sm leading-none"
            >
              <span class="mr-3"
                ><img src="assets/icon/shipping-icon.svg" alt="icon"
              /></span>
              <span v-if="product.shippingFree || totalPrice > 80">
                {{ $t("productDetails.freeShipping") }}
              </span>
              <span v-else>
                {{ $t("productDetails.paidShipping") }}
              </span>
            </li>
            <li
              v-if="availableStock > minPurchase"
              class="flex items-center text-arsenic font-bold text-sm leading-none"
            >
              <span class="mr-3"
                ><img src="assets/icon/ready-to-ship.svg" alt="icon"
              /></span>
              {{ $t("productDetails.readyToShip") }}
            </li>
            <li
              v-if="deliveryTime && product.available"
              class="flex items-center text-arsenic font-bold text-sm leading-none text-green-600 gap-2"
            >
              <span class="mr-2"
                ><img
                  src="assets/icon/correct-with-circle-icon.svg"
                  alt="icon"
                />
              </span>
              <span v-if="availableStock >= minPurchase && deliveryTime"
                >{{ $t("productDetails.deliveryTime") }}
              </span>
              <span
                v-else-if="
                  availableStock < minPurchase && deliveryTime && restockTime
                "
              >
                {{ $t("productDetails.deliveryTime") }} {{ restockTime }}
                {{ $t("productDetails.days") }}</span
              >
              -
              <span
                >{{ $t("productDetails.shippingTime") }}
                {{ deliveryTime?.translated?.name }}</span
              >
            </li>
            <li
              v-if="!product.available"
              class="flex items-center text-arsenic font-bold text-sm leading-none text-red-600 gap-2"
            >
              <span class="px-1.5"><i class="fa fa-xl fa-xmark"></i> </span>
              <span>{{ $t("productDetails.noAvailable") }}</span>
            </li>
          </ul>
        </div>
        <ProductDetailProductVariantConfigurator @change="changeVariant" />

        <ClientOnly>
          <div class="mb-4" v-if="Bundles && Bundles?.products?.length > 0">
            <b class="block mb-4">{{ $t("product.bundles.label") }}</b>
            <div class="flex gap-3 mb-4">
              <template v-for="(product, index) in Bundles.products">
                <BundleProductLineItem
                  :product="product"
                ></BundleProductLineItem>
              </template>
            </div>
            <template v-for="(product, index) in Bundles.products">
              <NuxtLink
                :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
                target="_blank"
                class="mb-2"
              >
                {{ getTranslatedProperty(product, "name") }} </NuxtLink
              ><br />
            </template>
          </div>
        </ClientOnly>

        <ProductDetailProductAddToCart
          v-if="!Bundles || Bundles?.products?.length < 1"
          :product="product"
        />
        <ProductDetailAddBundleToCart v-else :product="product" :bundles="Bundles.products" />

        <div class="add-wishlist mb-8">
          <!-- Add to Wishlist Button -->
          <button
            class="btn-default btn-transprent-bg bg-transparent w-full btn-w-icon justify-center"
            @click="toggleWishlistProduct"
          >
            {{ $t("productDetails.wishlistBtn") }}
            <span class="ml-5">
              <i v-if="isInWishlist" class="fa-heart fa-solid text-red-bg"></i>
              <i v-else class="fa-heart fa-regular"></i>
            </span>
          </button>
        </div>

        <!-- Payment Icons -->
        <div class="flex justify-between items-center mb-8">
          <template v-for="paymentMethod in paymentMediaUrls">
            <img
              :src="paymentMethod.url"
              :alt="paymentMethod.alt"
              loading="lazy"
              width="2"
              height="2"
              class="w-[55px] h-auto"
            />
          </template>
        </div>

        <div class="product-faq">
          <ProductDetailProductFaq :product="product" />
        </div>
      </div>
    </template>
  </div>
</template>
